import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ExamStatusBanner = ({ examStatus, onBookAppointment, retryCategories = [], categories = [], appointments = [], onStatusChange, cancellationCount = 0 }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (!examStatus || examStatus === 'not_ready') {
    return null;
  }

  // Get category names for retry
  const retryCategoryNames = retryCategories
    .map(catId => categories.find(c => c.id === catId)?.title)
    .filter(Boolean);

  // Get next upcoming appointment (not cancelled)
  const upcomingAppointment = appointments
    .filter(a => new Date(a.appointment_date) > new Date() && a.status !== 'cancelled')
    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))[0];

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
      time: date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handleCancelAppointment = async () => {
    if (!upcomingAppointment) return;
    setCancelling(true);
    try {
      await axios.patch(`${API}/appointments/appointments/${upcomingAppointment.id}/status`, {
        status: 'cancelled'
      });
      setShowCancelConfirm(false);
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setShowCancelConfirm(false);
      const msg = err.response?.data?.detail || 'Fehler beim Absagen des Termins';
      const { toast } = await import('sonner');
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const getStatusConfig = () => {
    switch (examStatus) {
      case 'ready_for_exam':
        return {
          color: 'bg-blue-50 border-blue-200',
          icon: '🎓',
          title: 'Bereit für Abschlussgespräch',
          message: 'Sie haben alle Pflicht-Module abgeschlossen! Buchen Sie jetzt Ihr Abschlussgespräch mit einem Vertriebsleiter.',
          action: 'Termin buchen',
          actionColor: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'exam_booked':
      case 'exam_scheduled':
        return {
          color: 'bg-green-50 border-green-200',
          icon: '📅',
          title: 'Termin gebucht',
          message: upcomingAppointment ? null : 'Ihr Abschlussgespräch ist geplant.',
          action: null,
          actionColor: null
        };
      case 'exam_passed':
        return {
          color: 'bg-green-50 border-green-200',
          icon: '🎉',
          title: 'Bestanden!',
          message: 'Herzlichen Glückwunsch! Sie haben das Abschlussgespräch bestanden.',
          action: null,
          actionColor: null
        };
      case 'exam_failed':
        return {
          color: 'bg-orange-50 border-orange-200',
          icon: '📚',
          title: 'Wiederholung erforderlich',
          message: retryCategoryNames.length > 0
            ? 'Bitte wiederholen Sie folgende Kategorien, um einen neuen Termin buchen zu können:'
            : 'Bitte wiederholen Sie die zugewiesenen Module und buchen Sie dann einen neuen Termin.',
          action: null,
          actionColor: null
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <>
      <div className={`border rounded-xl p-5 ${config.color} mb-6`} data-testid="exam-status-banner">
        <div className="flex items-start space-x-3">
          <div className="text-3xl">{config.icon}</div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">{config.title}</h3>
            {config.message && <p className="text-gray-700 text-sm mb-2">{config.message}</p>}

            {/* Inline appointment details for exam_booked */}
            {(examStatus === 'exam_booked' || examStatus === 'exam_scheduled') && upcomingAppointment && (
              <div className="mt-2 bg-white/60 rounded-lg p-3 space-y-2" data-testid="appointment-details-inline">
                <div className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">{formatDateTime(upcomingAppointment.appointment_date).date}</span>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{formatDateTime(upcomingAppointment.appointment_date).time} Uhr ({upcomingAppointment.duration_minutes} Min.)</span>
                </div>
                {upcomingAppointment.vertriebsleiter_name && (
                  <div className="flex items-center text-sm text-gray-700">
                    <svg className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>mit {upcomingAppointment.vertriebsleiter_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {upcomingAppointment.zoom_meeting_url && (
                    <a
                      href={upcomingAppointment.zoom_meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                      data-testid="zoom-meeting-link"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Zum Zoom Meeting
                    </a>
                  )}
                  {cancellationCount < 2 ? (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="inline-flex items-center px-3 py-1.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                      data-testid="cancel-appointment-btn"
                    >
                      Termin absagen {cancellationCount > 0 ? `(${2 - cancellationCount} verbleibend)` : ''}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-500 italic" data-testid="cancel-limit-reached">
                      Absage-Limit erreicht
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Retry categories for exam_failed */}
            {examStatus === 'exam_failed' && retryCategoryNames.length > 0 && (
              <div className="mt-2 mb-3 space-y-1">
                {retryCategoryNames.map((name, i) => (
                  <div key={i} className="flex items-center space-x-2 text-sm">
                    <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                    <span className="font-medium text-orange-800">{name}</span>
                  </div>
                ))}
                <p className="text-xs text-orange-600 mt-2">
                  Schliessen Sie alle Module in diesen Kategorien ab, um einen neuen Termin buchen zu können.
                </p>
              </div>
            )}
            
            {config.action && onBookAppointment && (
              <button
                onClick={onBookAppointment}
                className={`${config.actionColor} text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors`}
                data-testid="book-appointment-btn"
              >
                {config.action}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm" data-testid="cancel-confirm-modal">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Termin absagen?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Sind Sie sicher, dass Sie Ihren Termin absagen möchten? Sie können danach einen neuen Termin buchen.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
                data-testid="cancel-confirm-no"
              >
                Behalten
              </button>
              <button
                onClick={handleCancelAppointment}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="cancel-confirm-yes"
              >
                {cancelling ? 'Wird abgesagt...' : 'Ja, absagen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExamStatusBanner;
